import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  customerDefinitions,
  emptyCustomerFilters,
  subscriptionLabel,
  validateCustomerFilters,
  type CustomerFilters,
} from "./customer-table-data";

export function CustomerFiltersPanel({
  filters: f,
  onChange,
  tiers,
  tiersError,
}: {
  filters: CustomerFilters;
  onChange: (filters: CustomerFilters) => void;
  tiers: string[];
  tiersError: boolean;
}) {
  const error = validateCustomerFilters(f);
  const tierDropdown = useRef<HTMLDetailsElement>(null);
  useEffect(() => {
    const closeOutside = (event: PointerEvent) => {
      if (
        !tierDropdown.current?.contains(event.target as Node) &&
        tierDropdown.current
      )
        tierDropdown.current.open = false;
    };
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape" && tierDropdown.current?.open) {
        tierDropdown.current.open = false;
        tierDropdown.current.querySelector("summary")?.focus();
      }
    };
    document.addEventListener("pointerdown", closeOutside);
    document.addEventListener("keydown", closeOnEscape);
    return () => {
      document.removeEventListener("pointerdown", closeOutside);
      document.removeEventListener("keydown", closeOnEscape);
    };
  }, []);
  const set = (key: keyof CustomerFilters, value: string | string[]) =>
    onChange({ ...f, [key]: value });
  const selectClass =
    "h-9 w-full rounded-md border border-admin-card-border bg-admin-card px-2 text-sm text-admin-text-primary";
  const select = (
    key: "gender" | "wearables" | "active",
    label: string,
    options: [string, string][],
  ) => (
    <div className="min-w-0 space-y-1">
      <label
        className="text-xs text-admin-text-secondary"
        htmlFor={`customer-${key}`}
      >
        {label}
      </label>
      <select
        id={`customer-${key}`}
        className={selectClass}
        value={f[key]}
        onChange={(e) => set(key, e.target.value)}
      >
        <option value="">Alle</option>
        {options.map(([value, text]) => (
          <option key={value} value={value}>
            {text}
          </option>
        ))}
      </select>
    </div>
  );
  const range = (
    min: "minAge" | "minVisits",
    max: "maxAge" | "maxVisits",
    label: string,
  ) => (
    <fieldset className="min-w-0 space-y-1">
      <legend className="text-xs text-admin-text-secondary">{label}</legend>
      <div className="flex h-9 items-center overflow-hidden rounded-md border border-admin-card-border bg-admin-card focus-within:ring-2 focus-within:ring-admin-primary-focus">
        {[min, max].map((key, i) => (
          <div key={key} className="flex min-w-0 flex-1 items-center">
            <label className="sr-only" htmlFor={`customer-${key}`}>
              {i === 0 ? "Min" : "Maks"}
            </label>
            <Input
              id={`customer-${key}`}
              type="number"
              min="0"
              step="1"
              placeholder={i === 0 ? "Min" : "Maks"}
              className="h-8 min-w-0 rounded-none border-0 px-2 shadow-none focus-visible:ring-0"
              value={f[key]}
              onChange={(e) => set(key, e.target.value)}
            />
            {i === 0 && (
              <span aria-hidden="true" className="text-admin-text-subtle">
                –
              </span>
            )}
          </div>
        ))}
      </div>
      {error?.startsWith(label) && (
        <p role="alert" className="text-xs text-admin-danger-500">
          {error}
        </p>
      )}
    </fieldset>
  );
  return (
    <div className="rounded-md border border-admin-card-border bg-admin-card p-3">
      <div className="grid grid-cols-2 items-start gap-x-3 gap-y-2 md:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-[0.7fr_1fr_0.8fr_1.1fr_1.2fr_1fr_2fr_auto]">
        {select("gender", "Kjønn", [
          ["male", "Mann"],
          ["female", "Kvinne"],
          ["unknown", "Ukjent"],
        ])}
        {range("minAge", "maxAge", "Alder")}
        {select("wearables", "Wearables", [
          ["true", "Ja"],
          ["false", "Nei"],
        ])}
        {range("minVisits", "maxVisits", "Labbesøk (gjennomførte)")}
        <div className="min-w-0 space-y-1">
          <span
            id="customer-tiers-label"
            className="text-xs text-admin-text-secondary"
          >
            Abonnement
          </span>
          <details ref={tierDropdown} className="relative">
            <summary
              aria-labelledby="customer-tiers-label customer-tiers-value"
              className={`${selectClass} flex cursor-pointer list-none items-center justify-between gap-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-admin-primary-focus [&::-webkit-details-marker]:hidden`}
            >
              <span id="customer-tiers-value" className="truncate">
                {f.tiers.length === 0
                  ? "Alle"
                  : f.tiers.length === 1
                    ? subscriptionLabel(f.tiers[0])
                    : `${f.tiers.length} valgt`}
              </span>
              <ChevronDown
                aria-hidden="true"
                className="h-4 w-4 shrink-0 text-admin-text-subtle"
              />
            </summary>
            <div className="absolute left-0 z-30 mt-1 w-72 max-w-[calc(100vw-2rem)] rounded-md border border-admin-card-border bg-admin-card p-1 shadow-lg">
              <fieldset
                aria-label="Abonnementstyper"
                className="max-h-64 overflow-y-auto"
              >
                {[...new Set(["none", ...tiers, ...f.tiers])].map((tier) => (
                  <label
                    key={tier}
                    className="flex cursor-pointer items-center gap-2 rounded-sm px-2 py-2 text-sm text-admin-text-primary hover:bg-admin-card-hover"
                  >
                    <input
                      type="checkbox"
                      className="shrink-0 accent-admin-primary"
                      checked={f.tiers.includes(tier)}
                      onChange={(e) =>
                        set(
                          "tiers",
                          e.target.checked
                            ? [...f.tiers, tier]
                            : f.tiers.filter((v) => v !== tier),
                        )
                      }
                    />
                    <span className="break-words">
                      {subscriptionLabel(tier)}
                    </span>
                  </label>
                ))}
              </fieldset>
              <Button
                variant="ghost"
                size="sm"
                className="mt-1 w-full justify-start border-t border-admin-card-border"
                onClick={() => set("tiers", [])}
              >
                Nullstill abonnement
              </Button>
            </div>
          </details>
          {tiersError && (
            <p role="alert" className="text-xs text-admin-danger-500">
              Kunne ikke hente abonnementstyper. Prøv å laste siden på nytt.
            </p>
          )}
        </div>
        {select("active", "Aktivt abonnement", [
          ["true", "Ja"],
          ["false", "Nei"],
        ])}
        <fieldset className="col-span-2 min-w-0 space-y-1 2xl:col-span-1">
          <legend className="text-xs text-admin-text-secondary">
            Opprettet (UTC)
          </legend>
          <div className="flex h-9 items-center gap-1 overflow-hidden rounded-md border border-admin-card-border bg-admin-card px-2 focus-within:ring-2 focus-within:ring-admin-primary-focus">
            {(["createdFrom", "createdTo"] as const).map((key, i) => (
              <div key={key} className="flex min-w-0 flex-1 items-center gap-1">
                <label
                  className="text-[10px] text-admin-text-subtle"
                  htmlFor={`customer-${key}`}
                >
                  {i === 0 ? "Fra" : "Til"}
                </label>
                <Input
                  id={`customer-${key}`}
                  type="date"
                  className="h-8 min-w-0 rounded-none border-0 px-0 text-xs shadow-none focus-visible:ring-0"
                  value={f[key]}
                  onChange={(e) => set(key, e.target.value)}
                />
              </div>
            ))}
          </div>
          {error?.startsWith("Opprettet") && (
            <p role="alert" className="text-xs text-admin-danger-500">
              {error}
            </p>
          )}
        </fieldset>
        <Button
          variant="ghost"
          size="sm"
          className="mt-5 h-9 w-9 p-0"
          aria-label="Nullstill filtre"
          title="Nullstill filtre"
          onClick={() => onChange({ ...emptyCustomerFilters })}
        >
          <RotateCcw className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );
}

/** A keyboard- and touch-accessible definition, independent of hover. */
export function CustomerColumnInfo({
  definition,
}: {
  definition: keyof typeof customerDefinitions;
}) {
  return (
    <details className="relative inline-block font-normal">
      <summary
        className="list-none cursor-pointer rounded-full border border-admin-card-border w-4 h-4 text-center text-[10px] leading-[14px]"
        aria-label="Vis kolonnedefinisjon"
      >
        i
      </summary>
      <p className="absolute z-30 top-5 right-0 w-64 rounded-md border border-admin-card-border bg-admin-card p-3 shadow-lg text-xs text-admin-text-primary">
        {customerDefinitions[definition]}
      </p>
    </details>
  );
}
import { useEffect, useRef } from "react";
import { ChevronDown, RotateCcw } from "lucide-react";
